import { Button, Input, Tooltip, message as adMessage } from "antd";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { EditOutlined, SendOutlined } from "@ant-design/icons";
import {
  createFilesMessagePayload,
  createMessagePayload,
  getChatMembers,
} from "../helper";
import { selectUser } from "../../../../store/user/selectors";
import {
  sendWebSocketMessage,
  updateQuestionIndex,
} from "../../../../store/chats/slice";
import { StyledChatInputWrapper } from "./styles";
import FileUploadInput from "./FileUploadInput";
import httpClient from "../../../../api/httpClient";
import { parseError } from "../../../../utils/shared";
import { selectTopicQuestions } from "../../../../store/chats/selectors";
import SendMessageInput from "./SendMessageInput";
import { convertMentionsToIds } from "./helper";
import EmojiPicker from "./EmojiPicker";

const ChatInput = (props) => {
  const { onSendMessage, selectedChat, selectedTenantId } = props;
  const [message, setMessage] = useState("");
  const [isFormatting, setIsFormatting] = useState(false);
  const {
    questions: topicQuestions = [],
    conversation_id: question_conversation_id,
    currentQuestionIndex = 0,
    prevIndex,
  } = useSelector(selectTopicQuestions) || {};

  const dispatch = useDispatch();

  const user = useSelector(selectUser);

  useEffect(() => {
    (async () => {
      if (prevIndex !== undefined && prevIndex === currentQuestionIndex) return;

      const currentQuestion = topicQuestions?.[currentQuestionIndex]?.question;
      if (
        selectedChat?.id === question_conversation_id &&
        topicQuestions?.length &&
        currentQuestionIndex < topicQuestions.length &&
        currentQuestion
      ) {
        dispatch(
          sendWebSocketMessage(
            createMessagePayload({
              message: currentQuestion,
              selectedChat,
              user,
              is_bot: true,
            }),
          ),
        );
        setMessage("");
        onSendMessage();
      }

      dispatch(
        updateQuestionIndex({ index: currentQuestionIndex, isPrev: true }),
      );
    })();
  }, [
    currentQuestionIndex,
    topicQuestions,
    selectedChat?.id,
    question_conversation_id,
  ]);

  const users = useMemo(() => {
    return getChatMembers(selectedChat);
  }, [selectedChat]);

  const handleSendMessage = useCallback(
    (files) => {
      if (
        (!message?.trim() || !selectedChat?.id || !user?.user_id) &&
        !files?.length
      )
        return;

      const finalMessage = convertMentionsToIds(message, users);

      const payloadFunction = files?.length
        ? createFilesMessagePayload
        : createMessagePayload;

      dispatch(
        sendWebSocketMessage(
          payloadFunction({
            message: finalMessage,
            selectedChat,
            user,
            files,
          }),
        ),
      );

      onSendMessage();
      setMessage("");
      if (currentQuestionIndex < topicQuestions?.length) {
        setTimeout(() => {
          dispatch(updateQuestionIndex({ index: currentQuestionIndex + 1 }));
        }, 500);
      }
    },
    [
      message,
      selectedChat?.id,
      selectedTenantId,
      user?.user_id,
      dispatch,
      currentQuestionIndex,
      users,
    ],
  );

  const handleFormatText = useCallback(async () => {
    try {
      if (!message.trim()) return;

      setIsFormatting(true);

      const { data } = await httpClient.post("ai-service", {
        action: "format_text",
        payload: {
          text: message,
        },
      });

      setMessage(data?.formatted);
    } catch (error) {
      parseError(error, true);
    } finally {
      setIsFormatting(false);
    }
  }, [message]);

  const selectedQuestion = topicQuestions[currentQuestionIndex];

  return (
    <StyledChatInputWrapper>
      <div className="input-box">
        <div className="flex align-center">
          <FileUploadInput handleSendMessage={handleSendMessage} />
          <EmojiPicker
            onEmojiClick={({ emoji }) => setMessage(message + emoji)}
          />
        </div>
        <SendMessageInput
          message={message}
          setMessage={setMessage}
          question={selectedQuestion}
          disabled={isFormatting}
          handleSendMessage={handleSendMessage}
          users={users}
        />
        {(selectedQuestion?.type === "string" || !selectedQuestion) && (
          <Tooltip title="Format text professionally">
            <Button
              type="text"
              onClick={handleFormatText}
              disabled={!message.trim() || isFormatting}
              loading={isFormatting}
              icon={<EditOutlined />}
              shape="circle"
              className="edit-message-button"
            />
          </Tooltip>
        )}
        <Button
          type="primary"
          onClick={handleSendMessage}
          shape="circle"
          disabled={!message?.trim()}
          icon={<SendOutlined />}
        />
      </div>
    </StyledChatInputWrapper>
  );
};

export default ChatInput;
